import React from 'react';
import SEO from './SEO';

/**
 * Smart default Head component that automatically provides SEO based on the current page path.
 * This component is automatically used for ALL pages unless they export their own Head component.
 *
 * It intelligently detects:
 * - FlexEZ pages (any page under /flexez/) and uses flexez.* SEO defaults
 * - Individual pages with their own SEO keys in locales seo.json files
 * - Falls back to default.* SEO
 */

function PageHead({ location, pageContext, data, ...props }) {
  // Get language from pageContext
  const language = pageContext?.language || 'en';
  const pathname = location?.pathname || pageContext?.i18n?.originalPath || '/';

  // Get translation data from the page query
  const seoData = data?.locales?.edges?.find(edge => edge.node.ns === 'seo');
  const translations = seoData ? JSON.parse(seoData.node.data) : {};

  // Helper to get translation
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
      if (!value) break;
    }
    return value || defaultValue;
  };

  // Determine which SEO keys to use based on the pathname
  let seoKey = 'default';
  let pagePath = pathname;

  // Remove language prefix from path (e.g., /fr/about -> /about)
  const cleanPath = pathname.replace(/^\/(en|fr|ja|es)\//, '/');

  // Check if it's a FlexEZ page (including sub-pages)
  if (cleanPath.startsWith('/flexez')) {
    // For FlexEZ sub-pages, try to find specific SEO, otherwise fall back to flexez defaults
    if (cleanPath === '/flexez' || cleanPath === '/flexez/') {
      seoKey = 'flexez';
    } else {
      // For sub-pages like /flexez/solutions/desk-booking, try specific key first
      const subPath = cleanPath.replace('/flexez/', '').replace(/\//g, '.');
      // Check if translation exists for this specific page
      const hasSpecificSeo = t('flexez.' + subPath + '.title');
      seoKey = hasSpecificSeo ? 'flexez.' + subPath : 'flexez';
    }
    pagePath = cleanPath;
  } else {
    // Try to match specific pages
    const pathToKeyMap = {
      '/': 'home',
      '/aboutus': 'aboutus',
      '/solutions/cop': 'cop',
      '/contact-us': 'contact',
      '/privacy-policy': 'privacy',
    };

    seoKey = pathToKeyMap[cleanPath] || 'default';
    pagePath = cleanPath;
  }

  // Try to get page-specific SEO, fall back to defaults
  const title = t(`${seoKey}.title`) || t('default.title');
  const description = t(`${seoKey}.description`) || t('default.description');
  const keywords = t(`${seoKey}.keywords`) || t('default.keywords');

  return (
    <SEO
      title={title}
      description={description}
      keywords={keywords}
      pathname={pagePath}
      language={language}
      {...props}
    />
  );
}

export default PageHead;
